import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { uploadImage } from "../../../redux/reducers/postApiSlice";
import { commonPostPutFormData } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { FolderIcon, XIcon } from "../../../images/icons/icons";
import DotLoaders from "../../common-components/loaders/DotLoaders";

const CarryItemForm = ({
  isEdit,
  rowData,
  closeModal,
  userInfoData,
  callFunction,
  categoryOptions,
}) => {
  const dispatch = useDispatch();
  const { postPutLoadingFormData } = useSelector((state) => state.common);
  const { imageUploadLoading } = useSelector((state) => state.postApi);

  const initialFormValues = {
    label: isEdit && rowData?.label ? rowData.label : "",
    category_id: isEdit && rowData?.category_id ? rowData.category_id : "",
    isActive: isEdit && rowData?.status === "active" ? true : false,
    image: isEdit && rowData?.image ? rowData.image : "",
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [selectedCategory, setSelectedCategory] = useState(
    isEdit && rowData?.category_id
      ? categoryOptions?.find((opt) => opt.value === rowData.category_id) || null
      : null
  );
  const [formErrors, setFormErrors] = useState({});

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeCategory = (selected) => {
    setSelectedCategory(selected);
    setFormValues((prev) => ({
      ...prev,
      category_id: selected?.value || "",
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUploadImage(file);
    }
  };

  const handleUploadImage = async (file) => {
    const TOKEN = userInfoData?.token;

    try {
      const actionResult = dispatch(uploadImage({ file, TOKEN }));
      const result = await actionResult;

      if (result?.payload?.status) {
        setFormValues((prev) => ({
          ...prev,
          image: result?.payload?.image_url || "",
        }));
        toast.success("Image uploaded successfully!");
      } else {
        toast.error(result?.payload?.message || "Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image");
    }
  };

  const handleImageRemove = () => {
    setFormValues((prev) => ({
      ...prev,
      image: "",
    }));
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.label?.trim()) {
      errors.label = "Label is required";
    }
    if (!values.category_id) {
      errors.category_id = "Category is required";
    }
    return errors;
  };

  const handleSubmitForm = async (userData) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = isEdit ? "/addon/carryitems/update" : "/addon/carryitems";
    const token = userInfoData?.token || "";

    const formData = new FormData();
    for (const key in userData) {
      if (userData.hasOwnProperty(key)) {
        if (userData[key] instanceof File) {
          formData.append(key, userData[key]);
        } else {
          formData.append(key, userData[key]?.toString() || "");
        }
      }
    }

    const requestBody = formData;

    try {
      const result = await dispatch(
        commonPostPutFormData({ method, endpoint, requestBody, token })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message || "Carry item saved successfully!"
        );
        callFunction();
        closeModal();
      } else {
        const errorMessage =
          result?.error?.message ||
          result?.payload?.message ||
          "Failed to save carry item!";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error making POST request:", error);
      toast.error("Something went wrong!");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const userData = {
        label: formValues.label || "",
        category_id: formValues.category_id || "",
        status: formValues.isActive ? "active" : "inactive",
        image: formValues.image || "",
      };

      if (isEdit) {
        userData.uid = rowData?.uid || "";
      }

      const handleConfirm = async () => {
        await handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure!`,
        "Edit Form",
        "Submit Form"
      );
    }
  };

  return (
    <div className="addon-cat-form-modal">
      <div className="addon-cat-header">
        <h2 className="addon-cat-title">
          {isEdit ? "Edit Carry Item" : "Add Carry Items"}
        </h2>
        <button className="addon-cat-close-btn" onClick={closeModal}>
          <XIcon />
        </button>
      </div>

      <form className="addon-cat-form" onSubmit={onSubmit}>
        <div className="addon-cat-form-group">
          <label className="addon-cat-label">
            Label <span className="addon-cat-required">*</span>
          </label>
          <input
            type="text"
            name="label"
            value={formValues.label}
            onChange={onChangeInput}
            placeholder="Label"
            className={`addon-cat-input ${formErrors.label ? "error" : ""}`}
          />
          {formErrors.label && (
            <span className="addon-cat-error">{formErrors.label}</span>
          )}
        </div>

        <div className="addon-cat-form-group">
          <label className="addon-cat-label">
            Category <span className="addon-cat-required">*</span>
          </label>
          <Select
            value={selectedCategory}
            onChange={onChangeCategory}
            options={categoryOptions || []}
            placeholder="Category"
            className={`addon-cat-select ${formErrors.category_id ? "error" : ""}`}
            classNamePrefix="react-select"
            isClearable
          />
          {formErrors.category_id && (
            <span className="addon-cat-error">{formErrors.category_id}</span>
          )}
        </div>

        <div className="addon-cat-form-row">
          <div className="addon-cat-form-group addon-cat-image-group">
            <label className="addon-cat-label">
              Image <span className="addon-cat-required">*</span>
            </label>
            <div className="addon-cat-image-upload">
              {formValues.image ? (
                <div className="addon-cat-image-preview">
                  <img src={formValues.image} alt="Preview" />
                  <button
                    type="button"
                    className="addon-cat-image-remove"
                    onClick={handleImageRemove}
                  >
                    <XIcon />
                  </button>
                </div>
              ) : (
                <label className="addon-cat-upload-btn">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  {imageUploadLoading ? (
                    <DotLoaders />
                  ) : (
                    <>
                      <FolderIcon />
                      <span>CHOOSE FILE</span>
                    </>
                  )}
                </label>
              )}
              {!formValues.image && <span>No file chosen</span>}
            </div>
          </div>

          <div className="addon-cat-form-group addon-cat-toggle-group">
            <label className="addon-cat-toggle">
              <input
                type="checkbox"
                name="isActive"
                checked={formValues.isActive}
                onChange={onChangeInput}
              />
              <span className="addon-cat-toggle-slider"></span>
            </label>
            <span className="addon-cat-toggle-label">
              {formValues.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="addon-cat-form-actions">
          <button
            type="button"
            className="addon-cat-btn addon-cat-btn-secondary"
            onClick={closeModal}
            disabled={postPutLoadingFormData}
          >
            Close
          </button>
          <button
            type="submit"
            className="addon-cat-btn addon-cat-btn-primary"
            disabled={postPutLoadingFormData}
          >
            {postPutLoadingFormData ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CarryItemForm;
